import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    CreateUserProfilePropertiesResponse,
    CreateUserProfileRequest,
    CreateUserProfileResponse,
    CustomerIdentityApiResponse,
    GetUserProfileResponse,
    CustomerIdentityAuthCallbackResponse,
    CustomerIdentityAuthenticationMethodsResponse,
    CustomerIdentityEmailAuthMethodResponse,
    CustomerIdentityLegacyUserLookupResponse,
    CustomerIdentityMigrationResponse,
    CustomerIdentityPhoneAuthMethodResponse,
    CustomerIdentityTenantConfig,
    CustomerIdentityUserPropertiesResponse,
    CustomerIdentityVerifyEmailResponse,
    ENVIRONMENT,
    LegacyProvider,
    LegacyUserStatus,
    NativeSigninContinueResponse,
    NativeSignupContinueResponse,
    NativeSignupStartResponse,
    UpdateUserProfilePropertiesResponse,
} from '@ld.consumer-portal/models';
import { firstValueFrom, map, tap } from 'rxjs';
import { LoggingService } from './logging.service';
import { RemoteConfigService } from './remote-config.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerIdentityService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);
  private readonly remoteConfigService = inject(RemoteConfigService);

  private get baseUrl(): string {
    const useApim = this.remoteConfigService.config().use_apim_customer_identity;
    const apimUrl = this.env.appSettings.customerIdentityApiUrl ?? '';
    const directUrl = this.env.appSettings.customerIdentityDirectApiUrl ?? '';
    
    const selectedUrl = useApim ? apimUrl : directUrl;
    
    this.loggingService.logTrace(
      `[CustomerIdentityService] Using ${useApim ? 'APIM' : 'Direct'} endpoint: ${selectedUrl}`,
    );
    
    return selectedUrl;
  }

  async getTenantConfig(host: string): Promise<CustomerIdentityTenantConfig> {
    return firstValueFrom(
      this.http.get<CustomerIdentityApiResponse<CustomerIdentityTenantConfig>>(`${this.baseUrl}/api/TenantConfiguration`, {
        params: { Host: host },
      }).pipe(map(response => response.details)),
    );
  }

  async verifyEmail(email: string, tenantHost?: string): Promise<CustomerIdentityVerifyEmailResponse> {
    const body: Record<string, string> = { email };
    if (tenantHost) {
      body['tenantHost'] = tenantHost;
    }
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<CustomerIdentityVerifyEmailResponse>>(`${this.baseUrl}/api/VerifyEmail`, body)
        .pipe(map(response => response.details)),
    );
  }

  async getUserProperties(
    externalUserId: string,
    propertyGroups?: string,
  ): Promise<CustomerIdentityUserPropertiesResponse> {
    const params: Record<string, string> = { ExternalUserId: externalUserId };
    if (propertyGroups) {
      params['PropertyGroups'] = propertyGroups;
    }
    return firstValueFrom(
      this.http.get<CustomerIdentityApiResponse<CustomerIdentityUserPropertiesResponse>>(`${this.baseUrl}/api/UserProperties`, { params })
        .pipe(map(response => response.details)),
    );
  }

  async createPhoneAuthMethod(
    externalUserId: string,
    phoneFieldName: string,
    phoneType: string,
    entraTenantId: string,
  ): Promise<CustomerIdentityPhoneAuthMethodResponse> {
    const body = { externalUserId, phoneFieldName, phoneType, entraTenantID: entraTenantId };
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<CustomerIdentityPhoneAuthMethodResponse>>(
        `${this.baseUrl}/api/PhoneAuthenticationMethod`,
        body,
      ).pipe(map(response => response.details)),
    );
  }

  async createEmailAuthMethod(
    externalUserId: string,
    emailFieldNameOrAddress: string,
    entraTenantId: string,
    options?: { useEmailAddress?: boolean },
  ): Promise<CustomerIdentityEmailAuthMethodResponse> {
    const body = options?.useEmailAddress
      ? { externalUserId, emailAddress: emailFieldNameOrAddress, entraTenantID: entraTenantId }
      : { externalUserId, emailFieldName: emailFieldNameOrAddress, entraTenantID: entraTenantId };
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<CustomerIdentityEmailAuthMethodResponse>>(
        `${this.baseUrl}/api/EmailAuthenticationMethod`,
        body,
      ).pipe(map(response => response.details)),
    );
  }

  async getAuthenticationMethods(
    externalUserId: string,
    entraTenantId: string,
  ): Promise<CustomerIdentityAuthenticationMethodsResponse> {
    return firstValueFrom(
      this.http.get<CustomerIdentityApiResponse<CustomerIdentityAuthenticationMethodsResponse>>(
        `${this.baseUrl}/api/AuthenticationMethods`,
        {
          params: {
            ExternalUserId: externalUserId,
            EntraTenantID: entraTenantId,
          },
        },
      ).pipe(map(response => response.details)),
    );
  }

  async processCallback(
    sessionId: string,
    code: string,
    state: string,
  ): Promise<CustomerIdentityAuthCallbackResponse> {
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<CustomerIdentityAuthCallbackResponse>>(`${this.baseUrl}/api/AuthCallback`, {
        sessionId,
        code,
        state,
      }).pipe(map(response => response.details)),
    );
  }

  async legacyUserLookup(
    tenantGuid: string,
    email: string,
  ): Promise<CustomerIdentityLegacyUserLookupResponse> {
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<CustomerIdentityLegacyUserLookupResponse>>(`${this.baseUrl}/api/LegacyUserLookup`, {
        tenantGuid,
        email,
      }).pipe(
        tap(response => {
          this.loggingService.logTrace(
            `[CustomerIdentityService] LegacyUserLookup raw response for ${email}`,
            { found: response.details.found, hasMatches: !!response.details.matches, userStatus: response.details.userStatus ?? 'N/A', isActive: response.details.isActive ?? false },
          );
        }),
        map(response => {
          const details = response.details;
          if (details.found && !details.matches && details.userStatus) {
            this.loggingService.logInfo(
              `[CustomerIdentityService] Normalizing flat LegacyUserLookup response: userStatus=${details.userStatus}, isActive=${details.isActive}`,
            );
            const status = details.isActive ? LegacyUserStatus.Active : LegacyUserStatus.NotMigrated;
            const provider = details.userStatus === LegacyProvider.MLA ? LegacyProvider.MLA
              : details.userStatus === LegacyProvider.B2C ? LegacyProvider.B2C
              : details.userStatus === LegacyProvider.EPS ? LegacyProvider.EPS
              : details.userStatus;
            details.matches = [{ provider, status }];
            this.loggingService.logInfo(
              `[CustomerIdentityService] Normalized matches: provider=${provider}, status=${status}`,
            );
          }
          if (!details.matches) {
            details.matches = [];
            this.loggingService.logTrace('[CustomerIdentityService] No matches found, defaulting to empty array');
          }
          return details;
        }),
      ),
    );
  }

  async startMigration(
    tenantGuid: string,
    email: string,
    password: string,
    legacyProvider: string,
    includeExecutionDetails?: boolean,
    firstName?: string,
    lastName?: string,
    newPassword?: string,
  ): Promise<CustomerIdentityMigrationResponse> {
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<CustomerIdentityMigrationResponse>>(`${this.baseUrl}/api/StartMigration`, {
        tenantGuid,
        email,
        password,
        legacyProvider,
        includeExecutionDetails: includeExecutionDetails ?? false,
        firstName,
        lastName,
        ...(newPassword && { newPassword }),
      }).pipe(map(response => response.details)),
    );
  }

  async nativeSignupStart(
    email: string,
    entraTenantID: string,
    tenantHost?: string,
    firstName?: string,
    lastName?: string,
  ): Promise<NativeSignupStartResponse> {
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<NativeSignupStartResponse>>(`${this.baseUrl}/api/NativeSignupStart`, {
        email,
        entraTenantID,
        tenantHost,
        firstName,
        lastName,
      }).pipe(map(response => response.details)),
    );
  }

  async nativeSignupContinue(
    continuationToken: string,
    entraTenantID: string,
    otp?: string,
    password?: string,
    firstName?: string,
    lastName?: string,
    email?: string,
  ): Promise<NativeSignupContinueResponse> {
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<NativeSignupContinueResponse>>(`${this.baseUrl}/api/NativeSignupContinue`, {
        continuationToken,
        entraTenantID,
        otp,
        password,
        firstName,
        lastName,
        email,
      }).pipe(map(response => response.details)),
    );
  }

  async nativeSigninContinue(
    signinContinuationToken: string,
    entraTenantID: string,
    otp: string,
  ): Promise<NativeSigninContinueResponse> {
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<NativeSigninContinueResponse>>(`${this.baseUrl}/api/NativeSigninContinue`, {
        signinContinuationToken,
        entraTenantID,
        otp,
      }).pipe(map(response => response.details)),
    );
  }

  async getUserProfileByExternalUserId(externalUserId: string): Promise<GetUserProfileResponse> {
    return firstValueFrom(
      this.http.get<CustomerIdentityApiResponse<GetUserProfileResponse>>(
        `${this.baseUrl}/api/UserProfile`,
        { params: { ExternalUserId: externalUserId } },
      ).pipe(map(response => response.details)),
    );
  }

  async createUserProfile(request: CreateUserProfileRequest): Promise<CreateUserProfileResponse> {
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<CreateUserProfileResponse>>(
        `${this.baseUrl}/api/UserProfile`,
        request,
      ).pipe(map(response => response.details)),
    );
  }

  async createUserProfileProperties(
    externalUserId: string,
    propertiesJson: string,
  ): Promise<CreateUserProfilePropertiesResponse> {
    const normalizedJson = this.toFlatPropertyArray(propertiesJson);
    return firstValueFrom(
      this.http.post<CustomerIdentityApiResponse<CreateUserProfilePropertiesResponse>>(
        `${this.baseUrl}/api/UserProfileProperties`,
        { externalUserId, propertiesJson: normalizedJson },
      ).pipe(map(response => response.details)),
    );
  }

  async updateUserProfileProperties(
    externalUserId: string,
    propertiesJson: string,
  ): Promise<UpdateUserProfilePropertiesResponse> {
    const normalizedJson = this.toFlatPropertyArray(propertiesJson);
    return firstValueFrom(
      this.http.put<CustomerIdentityApiResponse<UpdateUserProfilePropertiesResponse>>(
        `${this.baseUrl}/api/UserProfileProperties`,
        { externalUserId, propertiesJson: normalizedJson },
      ).pipe(map(response => response.details)),
    );
  }

  private toFlatPropertyArray(propertiesJson: string): string {
    const parsed: unknown = JSON.parse(propertiesJson);
    if (Array.isArray(parsed)) return propertiesJson;

    const entries: { FieldName: string; GroupName: string; GroupInstanceId: number; Value: string }[] = [];
    for (const [groupName, fields] of Object.entries(parsed as Record<string, Record<string, string>[]>)) {
      for (const fieldObj of fields) {
        for (const [fieldName, value] of Object.entries(fieldObj)) {
          entries.push({ FieldName: fieldName, GroupName: groupName, GroupInstanceId: 1, Value: String(value) });
        }
      }
    }
    return JSON.stringify(entries);
  }

  async resolveGlobalUserId(externalUserId: string): Promise<string | null> {
    try {
      const result = await this.getUserProperties(externalUserId);
      const properties = JSON.parse(result.propertiesJson) as Record<string, unknown>;

      const searchEntries = (
        entries: Record<string, string>[],
        source: string,
      ): string | null => {
        for (const entry of entries) {
          const guid = entry['ServicingCustomerGuid'] || entry['CustomerGuid'];
          if (guid) {
            this.loggingService.logInfo(
              `[CustomerIdentityService] Resolved GlobalUserId for externalUserId=${externalUserId} (source=${source})`,
            );
            return guid;
          }
        }
        return null;
      };

      for (const [key, value] of Object.entries(properties)) {
        if (Array.isArray(value)) {
          const found = searchEntries(value as Record<string, string>[], key);
          if (found) return found;
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
          for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
            if (Array.isArray(nestedValue)) {
              const found = searchEntries(nestedValue as Record<string, string>[], `${key}.${nestedKey}`);
              if (found) return found;
            }
          }
        }
      }

      this.loggingService.logWarning(
        `[CustomerIdentityService] No ServicingCustomerGuid found in any property group for externalUserId=${externalUserId}`,
      );
      return null;
    } catch (error) {
      this.loggingService.logException(
        error,
        `[CustomerIdentityService] Failed to resolve GlobalUserId for externalUserId=${externalUserId}`,
      );
      return null;
    }
  }
}
